/* eslint-disable complexity */
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import Kbd from 'design-system/Kbd';
import TaskComponent, { TaskProps } from 'react-scale/components/Task/TaskComponent';
import { AutosaveHook } from 'react-scale/hooks/useAutosave';
import Markdown from '@scale/frontend-lib/markdown';
import { TaskType } from '@scale/shared-types';
import { normalizeAssignment } from '@scale/shared/assignmentJson/legacy/assignment-normalizer';
import transformMultiTaskResponseToAssignment from '@scale/shared/assignmentJson/transformMultiTaskResponseToAssignment';
import { isTaskType2D, isVideoPlayback } from '@scale/shared/defaults';
import { MultiTurnInteractionConfiguration } from '@scale/shared/types/chat';
import { SpeedAuditLookupResponse } from '@scale/shared/types/corp/attemptAudit';
import { AuditPerAnnotation } from '@scale/shared/types/props/audit';
import { DiffView } from '@scale/shared/types/training';
import classnames from 'classnames';
import Combokeys from 'combokeys';
import { Button, CircularProgress } from 'design-system';
import { Modal, ModalBody, ModalHeader } from 'design-system/Modal';
import _ from 'lodash';
/* eslint-disable restrict-imports/restrict-imports */
import NucleusButton from 'nucleus/components/NucleusButton';
import React from 'react';
import InstructionFeedbackFormWrapper from 'react-scale/components/scalers/tasks/common/InstructionFeedbackFormWrapper';
import TaskerContextPane from 'react-scale/components/Task/TaskerContextPane';
import RapidDraggableSwitch, {
  DraggingOptions,
} from 'react-scale/dashboard.scale.com/components/SelfServe/Util/RapidDraggableSwitch';
import { ICompleteReviewAssignmentArgs } from 'react-scale/redux/actions';
import { AuditHeaderFooterInfo } from '../audits/CustomerAudit/types';
import { SupplementedMultiTaskResponseAssignmentWrapper } from './SupplementedMultiTaskResponseAssignmentWrapper';

export interface ResponseShortcut {
  title: string;
  faIcon: IconProp;
  onClick: () => void;
}
interface MultiTaskResponseProps {
  taskComponentClassName?: string;
  renderKey?: string;
  subtask: $FIXME;
  responses: SpeedAuditLookupResponse[];
  initialSelected?: number;
  customDivHeight?: string;
  config?: $FIXME;
  hideLabels?: boolean;
  auditCorruptions?: boolean;
  isCustomer?: boolean;
  isRealCustomerAudit?: boolean;
  isSpeedAudit?: boolean;
  taskTypeOverride?: TaskType;
  hideLifetimeEarnings?: boolean;
  handleIdSelect?: (x: string | null) => void;
  auditPerAnnotation?: AuditPerAnnotation;
  onNavChange?: (response: SpeedAuditLookupResponse, index: number) => void;
  auditHeaderFooterInfo?: AuditHeaderFooterInfo;
  taskRef?: $FIXME;
  benchmarkPage?: boolean;
  readOnly?: boolean;
  draggingOptions?: DraggingOptions; // define response.order above when this is defined
  responseShortcuts?: ResponseShortcut[];
  hideLeftSidebar?: boolean;
  feedbackCommentMarkdown?: string;
  showTaskerPane?: boolean;
  isDocumentAIGo?: boolean;
  showCalibrationFeedback?: boolean;
  runningValidators?: boolean;
  onRunValidators?: () => void;
  onSubmit?: (args: ICompleteReviewAssignmentArgs) => Promise<void>;
  setAutosaveRef?: (ref: AutosaveHook<any> & { discardAutosaveWithPrompt: () => void }) => void;
  hideTaskerInfo?: boolean;
}

interface MultiTaskResponseState {
  selectedIndex: number;
  showingProblemZonesInfo: boolean;
  initialFrame: number | undefined;
  showNER: boolean;
  openInstructionsModal: boolean;
}

class MultiTaskResponse extends React.Component<MultiTaskResponseProps, MultiTaskResponseState> {
  private combokeys?: Combokeys.Combokeys;

  constructor(props: MultiTaskResponseProps) {
    super(props);

    this.state = {
      selectedIndex: props.initialSelected || 0,
      showingProblemZonesInfo: false,
      initialFrame: undefined,
      showNER: false,
      openInstructionsModal: false,
    };
  }

  componentDidMount() {
    if (this.props.subtask.disableShortcuts) {
      return;
    }

    // It should not be necessary when everything is properly typed.
    if (!this.props.responses) {
      return;
    }

    this.combokeys = new Combokeys(document.documentElement);
    this.updateComboKeys();
  }

  componentDidUpdate(prevProps: MultiTaskResponseProps) {
    if (prevProps.responses.length !== this.props.responses.length) {
      this.updateComboKeys();
    }
  }

  shouldComponentUpdate(nextProps: MultiTaskResponseProps, nextState: Record<string, any>) {
    if (nextProps.subtask.id !== this.props.subtask.id && nextState.initialFrame !== undefined) {
      this.setState({ initialFrame: undefined });
      return false;
    }
    return true;
  }

  componentWillUnmount() {
    if (this.combokeys) {
      this.combokeys.detach();
    }
  }

  updateComboKeys() {
    if (!this.combokeys) {
      return;
    }

    this.combokeys.reset();
    for (let i = 0; i < this.props.responses.length; i++) {
      this.combokeys.bind((i + 1).toString(), () => this.handleResponseIndexSelect(i));
    }
  }

  handleResponseIndexSelect = (index: number) => {
    this.setState({
      selectedIndex: index,
    });
    if (this.props.onNavChange) {
      this.props.onNavChange(this.props.responses[index], index);
    }
  };

  handleResponseSelect = (name: string) => {
    const responseIndex = _.findIndex(this.props.responses, r => r.name === name);
    if (responseIndex > -1) {
      this.handleResponseIndexSelect(responseIndex);
    }
  };

  toggleProblemZonesInfo = () => {
    this.setState({ showingProblemZonesInfo: !this.state.showingProblemZonesInfo });
  };

  setInitialFrame = (initialFrame: number) => {
    this.setState({ initialFrame });
  };

  renderProblemZonesTooltip = () => {
    // I'd rather use Tooltip but they don't play well with modals ¯\_(ツ)_/¯
    return (
      <>
        <Modal
          maxWidth="400px"
          isOpen={this.state.showingProblemZonesInfo}
          onRequestClose={this.toggleProblemZonesInfo}
        >
          <ModalHeader onRequestClose={this.toggleProblemZonesInfo}>Problem Zones Info</ModalHeader>
          <ModalBody>
            Show annotation errors. Extraneous annotations are blue, missing annotations are green,
            and annotations with incorrect labels/attributes/position are red.
          </ModalBody>
        </Modal>
        Problem Zones{' '}
        <div
          onClick={this.toggleProblemZonesInfo}
          style={{ cursor: 'pointer', paddingLeft: '5px' }}
        >
          <FontAwesomeIcon icon="info-circle" />
        </div>
      </>
    );
  };

  getCurAttempt() {
    // If we are looking at an attempt from `subtask.attempts`, return it.
    // Otherwise arbitrarily return the last attempt from that list;
    // this is a good assumption for speed audits, but might not hold elsewhere.
    const curResponse = this.props.responses[this.state.selectedIndex];
    const response =
      this.props.subtask.attempts.find((attempt: any) => attempt._id === curResponse.id) ??
      this.props.subtask.attempts.slice(-1)[0];
    return typeof response === 'string' ? { _id: response, attemptedBy: response } : response;
  }

  renderNav() {
    return (
      <div className="nav-container">
        {this.state.openInstructionsModal && (
          <InstructionFeedbackFormWrapper
            subtask={this.props.subtask.id}
            openModal={this.state.openInstructionsModal}
            onClose={() => this.setState({ openInstructionsModal: false })}
            labels={[]}
            setSubmit={() => {}}
            readOnly
          />
        )}
        <div className="response-header">
          {this.props.draggingOptions ? (
            <div className="nucleus-nav">
              <RapidDraggableSwitch
                values={this.props.responses.map((r, i) => ({
                  id: i.toString(),
                  display: r.displayName || r.name,
                  value: i.toString(),
                  order: r.order!,
                }))}
                currentValue={this.state.selectedIndex.toString()}
                onSwitch={value => this.handleResponseIndexSelect(parseInt(value))}
                draggingOptions={this.props.draggingOptions}
              />
            </div>
          ) : (
            <div className="responses-nav">
              {_.map(this.props.responses, (r, i) => (
                <div
                  key={r.name ?? i}
                  className={classnames('responses-nav-item', {
                    isActive: this.state.selectedIndex === i,
                    isInactive: this.state.selectedIndex !== i,
                  })}
                  onClick={() => this.handleResponseIndexSelect(i)}
                >
                  {r.name === DiffView.ProblemZones
                    ? this.renderProblemZonesTooltip()
                    : r.displayName || r.name}
                  {!this.props.subtask.disableShortcuts && <Kbd>{+i + 1}</Kbd>}
                </div>
              ))}
              {this.props.onRunValidators && (
                <NucleusButton
                  secondary
                  className="validator-icon"
                  tooltipText={!this.props.runningValidators ? 'Run Validators' : null}
                  onClick={this.props.onRunValidators}
                >
                  {this.props.runningValidators ? (
                    <CircularProgress size={16} />
                  ) : (
                    <FontAwesomeIcon icon="play" />
                  )}
                </NucleusButton>
              )}
              {this.props.showCalibrationFeedback && (
                <Button
                  onClick={() =>
                    this.setState({ openInstructionsModal: !this.state.openInstructionsModal })
                  }
                >
                  Instruction feedback
                </Button>
              )}
              <div style={{ flexGrow: 1 }} />
              {this.props.showTaskerPane && this.props.subtask.attempts.length > 0 && (
                <TaskerContextPane
                  key={this.getCurAttempt()?.id}
                  attempt={this.getCurAttempt()}
                  projectId={this.props.subtask.projectId}
                  hideLifetimeEarnings={this.props.hideLifetimeEarnings}
                  taskMetadata={this.props.subtask.taskMetadata}
                  hideTaskerInfo={this.props.hideTaskerInfo}
                />
              )}
            </div>
          )}
        </div>
        {this.props.responseShortcuts && (
          <div className="response-shortcut-list">
            {this.props.responseShortcuts.map((responseShortcut: ResponseShortcut, ind: number) => (
              <a
                key={ind}
                className={classnames('response-shortcut')}
                onClick={() => responseShortcut.onClick()}
              >
                <FontAwesomeIcon icon={responseShortcut.faIcon} /> &nbsp;
                {responseShortcut.title}
              </a>
            ))}
          </div>
        )}
        <style jsx>{`
          .validator-icon: {
            margin-left: 8,
            min-width: 48,
          }

          .nav-container {
            display: flex;
            align-items: center;
          }

          .response-header {
            flex-grow: 1;
          }

          .response-shortcut-list {
            flex-grow: 0;
            padding-right: 16px;
          }

          .response-shortcut {
            margin-left: 8px;
          }

          .nucleus-nav {
            margin-bottom: 16px;
          }

          .responses-nav {
            overflow: hidden;
            background: white;
            border-bottom: 1px solid var(--color-RemoGray01);
            font-size: 15px;

            flex-shrink: 0;
            padding: 0;

            height: 52px;
            display: flex;
            align-items: center;
            overflow-x: auto;
          }

          .responses-nav-item {
            position: relative;
            margin-right: 12px;
            padding: 0 8px;
            display: flex;
            align-items: center;
            cursor: pointer;
            min-width: 100px;
          }

          .responses-nav-item.isInactive {
            opacity: 0.5;
          }

          .responses-nav-item.isActive {
            font-weight: bold;
            cursor: default;
          }

          .responses-nav-item.isActive:after {
            z-index: 1;
            top: 100%;
            left: 50%;
            border: solid transparent;
            content: ' ';
            height: 0;
            width: 0;
            position: absolute;
            pointer-events: none;
            border-color: rgba(255, 255, 255, 0);
            border-top-color: #fff;
            border-width: 16px;
            margin-left: -16px;
          }
        `}</style>
      </div>
    );
  }

  renderResponse() {
    const responseObj: SpeedAuditLookupResponse =
      this.props.responses[this.state.selectedIndex] ?? {};
    if (responseObj?.binary) {
      return (
        <div className="binary">
          <strong className="outcome">{responseObj.binary.outcome}</strong>
          <style jsx>{`
            .binary {
              flex-grow: 1;
              display: flex;
              align-items: center;
              justify-content: center;
              background: var(--bg-gray-light-1);
            }

            .outcome {
              opacity: 0.25;
              font-size: 24px;
            }
          `}</style>
        </div>
      );
    }

    if (responseObj?.feedbackCommentMarkdown) {
      return (
        <div className="feedback">
          <div
            dangerouslySetInnerHTML={{
              __html: Markdown.render(responseObj?.feedbackCommentMarkdown),
            }}
          ></div>
          <style jsx>{`
            .feedback {
              flex-grow: 1;
              border-top: 1px solid var(--color-RemoGray01);
              display: flex;
              flex-direction: column;
              min-height: 0;
              overflow: scroll;
              padding: 32px;
            }
          `}</style>
        </div>
      );
    }

    const taskProps: TaskProps = normalizeAssignment(this.props.subtask) as TaskProps;
    if (responseObj) {
      taskProps.fixResponse = responseObj.response;
      taskProps.lintResults = responseObj.confidenceScores?.lintResults;
      taskProps.graderResponse = responseObj.graderResponse;
      taskProps.graderDiffResponse = responseObj.graderDiffResponse;
      taskProps.isFrameRejected = responseObj.isFrameRejected;
      taskProps.isAnswerKey = responseObj.isAnswerKey;
      taskProps.isCustomer = this.props.isCustomer;
      taskProps.prepopulatedBreakdownColors = responseObj.prepopulatedBreakdownColors;
      taskProps.prepopulatedAttributeDefinitions = responseObj.prepopulatedAttributeDefinitions;
      taskProps.diffState = responseObj.diffState;
    }
    if (this.props.responses.length > 0) {
      taskProps.taskerResponseInFeedback = this.props.responses[0].response;
    }
    taskProps.readOnly = _.get(this.props, 'readOnly', true);
    taskProps.handleResponseSelect = this.handleResponseSelect;
    taskProps.handleIdSelect = this.props.handleIdSelect;
    taskProps.benchmarkPage = this.props.benchmarkPage;

    // For semseg audit
    taskProps.initializeInAdvancedView = true;
    taskProps.isReviewMode = true;
    taskProps.isPreviewMode = true;
    taskProps.showHeader = false;
    taskProps.config = taskProps.config || this.props.config;
    taskProps.auditCorruptions = this.props.auditCorruptions;
    taskProps.multiTaskResponse = true;
    taskProps.previousGraderParams = this.props.subtask.graderParams;
    taskProps.customDivHeight = this.props.customDivHeight;
    taskProps.hideLabels = this.props.hideLabels;
    taskProps.standalone = false;
    taskProps.isRealCustomerAudit = this.props.isRealCustomerAudit;
    taskProps.isSpeedAudit = this.props.isSpeedAudit;
    if (this.props.taskTypeOverride) {
      // Specifically for lidarlinking audit page, that merges the task and subtask properties
      taskProps.type = this.props.taskTypeOverride;
    }

    const taskType =
      this.props.taskTypeOverride || this.props.subtask.type || this.props.subtask.taskType;

    // For lidar
    taskProps.forceLoadFromSnapshot = true;
    taskProps.cuboidPathSceneName = responseObj?.id;

    // For audit.
    taskProps.auditPerAnnotation = this.props.auditPerAnnotation;

    // For SME audit
    if (this.props.isSpeedAudit && !this.props.readOnly) {
      taskProps.isPreviewMode = false;
      taskProps.onSubmit = this.props.onSubmit;
      taskProps.setAutosaveRef = this.props.setAutosaveRef;
      taskProps.fixable = true;
      taskProps.canReturnToQueue = true;
      taskProps.hideHeader = true;
      taskProps.attemptId = this.getCurAttempt()?.id;
    }
    // For DocumentTranscription (and potentially others)
    taskProps.initialFrame = this.state.initialFrame;
    taskProps.setInitialFrame = this.setInitialFrame;

    // For MultiModalTranscription
    if (taskType === TaskType.MultiModalTranscription) {
      taskProps.showNER = this.state.showNER;
      taskProps.setShowNER = value => this.setState({ showNER: value });
    }

    // For DocumentModel
    taskProps.model_results_detail = this.props.subtask.model_results_detail;
    taskProps.document_pages = this.props.subtask.attachmentS3Downloads
      ? this.props.subtask.attachmentS3Downloads
          .filter((attachment: any) => attachment.isConvertedImage)
          .map((attachment: any) => {
            return {
              height: attachment.metadata.height,
              width: attachment.metadata.width,
            };
          })
      : [];

    // Don't need this after CustomerAuditDeprecation migration
    if (this.props.auditHeaderFooterInfo) {
      taskProps.auditHeaderFooterInfo = {
        ...this.props.auditHeaderFooterInfo,
        navBar: this.renderNav(),
      };
    }

    if (
      this.props.taskTypeOverride === TaskType.TextCollection &&
      _.get(taskProps, 'customer_review_status') === 'fixed'
    ) {
      const taskerResponse = _.get(taskProps, 'responseHistory[0].response.annotations');
      const fixedResponse = _.get(taskProps, 'fixResponse.annotations');

      if (taskerResponse && fixedResponse) {
        const diffFieldIds = [];
        for (const fieldId of Object.keys(fixedResponse)) {
          // ignore signedUrl in our comparson of fields
          if (
            !_.isEqualWith(
              taskerResponse[fieldId]?.response[0],
              fixedResponse[fieldId]?.response[0],
              (v1, v2, key) => {
                return key === 'signedUrl' ? true : undefined;
              },
            )
          ) {
            diffFieldIds.push(fieldId);
          }
        }

        for (const fieldId of diffFieldIds) {
          responseObj.response.annotations[fieldId].isFixed = true;
        }
      }
    }

    // TODO(albert): Move this property into ChatAudit view Context
    if (taskType === TaskType.Chat && taskProps.params) {
      (taskProps.params as unknown as MultiTurnInteractionConfiguration).defaultExpand = true;
    }

    /*
     * For GenAI, when viewing a task's response history, we should view each attempt at the review level
     * it was completed in. For view responses, we will still use the current review level that the task is in.
     */
    if (
      responseObj &&
      [
        TaskType.Chat,
        TaskType.ChatExperimental,
        TaskType.ChatV2,
        TaskType.TextCollection,
        TaskType.MultiChat,
      ].includes(taskType)
    ) {
      if (!_.isNil(responseObj.id)) {
        taskProps.attemptId = responseObj.id;
      }

      if (!_.isNil(responseObj.attemptedAtReviewLevel)) {
        this.props.subtask.reviewLevel = responseObj.attemptedAtReviewLevel;
      }

      if (!_.isNil(responseObj.taskParamsFromV3Node)) {
        (taskProps.params as unknown as MultiTurnInteractionConfiguration) =
          responseObj.taskParamsFromV3Node;
      }
    }

    const assignment = transformMultiTaskResponseToAssignment(
      this.props.subtask,
      !!this.props.isRealCustomerAudit,
      responseObj,
    );

    const updateByResponseKey =
      (!isTaskType2D(taskType) && !isVideoPlayback(taskType)) ||
      taskType === TaskType.SegmentAnnotation;
    const responseKey = `${this.props.renderKey ?? ''}${
      updateByResponseKey ? _.get(responseObj, 'name') : 'responseKey'
    }`;
    const taskRef = this.props.taskRef || null;

    return (
      <div
        className="responses__body"
        style={this.props.customDivHeight ? { height: this.props.customDivHeight } : {}}
      >
        <React.Suspense fallback={null}>
          <SupplementedMultiTaskResponseAssignmentWrapper
            assignment={assignment}
            render={supplementedAssignment => (
              <TaskComponent
                responseKey={responseKey}
                taskType={taskType}
                taskProps={{
                  ...taskProps,
                  assignment: supplementedAssignment,
                  taskComponentClassName: this.props.taskComponentClassName,
                }}
                compact={true}
                taskRef={taskRef}
                hideLeftSidebar={this.props.hideLeftSidebar}
                isDocumentAIGo={this.props.isDocumentAIGo}
              />
            )}
          />
        </React.Suspense>
        <style jsx global>{`
          .responses__body,
          .responses__body .task {
            flex-grow: 1;
            display: flex;
            flex-direction: column;
            min-height: 0;
            overflow: auto;
            position: relative;
          }
        `}</style>
      </div>
    );
  }

  render() {
    return (
      <div className="responses">
        {/* Don't need this after CustomerAuditDeprecation migration. Only render nav bar
        if the nav bar won't be rendered by the specific {task type}{attempt|review|audit} components */}
        {!this.props.auditHeaderFooterInfo && this.renderNav()}
        {this.renderResponse()}
        <style jsx>{`
          .responses {
            flex-grow: 1;
            border-top: 1px solid var(--color-RemoGray01);
            display: flex;
            flex-direction: column;
            min-height: 0;
            overflow: auto;
          }
        `}</style>
      </div>
    );
  }
}

export default MultiTaskResponse;
